import * as React from 'react';
import { cx } from 'linaria';
import { TableOptions, useTable, useSortBy, HeaderGroup, TableInstance, TableState, Row } from 'react-table';

import { IconArrowDown } from '../foundation/icons';

import { states, Spinner } from '../index';
import { useEffect, useRef } from 'react';
import {
    alternatingRows,
    plainHeader,
    sortableHeader,
    sortableHeaderLabel,
    sortableHeaderLabelRightArrow,
    tableStyle,
} from './tables.styles';

type SortableHeaderLabelProps = {
    active: boolean;
    direction: 'asc' | 'desc';
};

export const SortableHeaderLabel: React.FunctionComponent<React.PropsWithChildren<SortableHeaderLabelProps>> = ({
    active,
    direction,
    children,
}) => {
    return (
        <div className={cx(sortableHeaderLabel)}>
            {children}
            {!active ? null : direction === 'asc' ? (
                <IconArrowDown
                    className={cx(sortableHeaderLabelRightArrow)}
                    height={14}
                    width={14}
                    style={{ transform: 'rotate(180deg)' }}
                />
            ) : (
                <IconArrowDown className={cx(sortableHeaderLabelRightArrow)} height={14} width={14} />
            )}
        </div>
    );
};

export type TableMapColumnIdToNameFunction = (column: HeaderGroup) => string;

export interface TableProps<T extends Record<string, unknown>> extends React.PropsWithChildren<TableOptions<T>> {
    mapColumnIdToName?: TableMapColumnIdToNameFunction;
    handleStateChange?: (state: TableState<Record<string, unknown>>) => void;
    hasColumnGotCustomSorting?: (column: HeaderGroup<Record<string, unknown>>) => boolean;
    customSortedColumnHeader?: (column: HeaderGroup<Record<string, unknown>>) => React.ReactNode;
    hasCustomRowProps?: boolean;
    getCustomRowProps?: (row: Row) => Record<string, unknown>;
    requestMoreData?: () => void;
    hasMoreData?: boolean;
    alternatingRowColors?: boolean;
}

const comparer = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
});

const getSortLabelHoverText = (column: HeaderGroup, mapColumnIdToName: TableMapColumnIdToNameFunction): string => {
    if (!(column as any).canSort) {
        return '';
    }

    if (!(column as any).isSorted) {
        return `Sort by ${mapColumnIdToName(column)} Ascending`;
    }

    if ((column as any).isSortedDesc) {
        return `Sort by ${mapColumnIdToName(column)} Ascending`;
    } else {
        return `Sort by ${mapColumnIdToName(column)} Descending`;
    }
};

export function Table<T extends Record<string, unknown>>(props: TableProps<T>): React.ReactElement {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state, columns } = useTable(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        {
            ...props,
            sortTypes: {
                alphanumeric: (row1: any, row2: any, columnName: any) => {
                    const rowOneCell = row1.values[columnName] || '0';
                    const rowTwoCell = row2.values[columnName] || '0';

                    return comparer.compare(rowOneCell, rowTwoCell);
                },
            },
        } as any,
        useSortBy,
    ) as TableInstance<Record<string, unknown>>;

    React.useEffect(() => {
        props.handleStateChange?.(state);
    }, [state]);

    const spinnerRef = useRef(null);

    useEffect(() => {
        if (props.requestMoreData) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (props.hasMoreData) {
                            props.requestMoreData();
                        }
                    }
                });
            });

            if (spinnerRef.current) {
                observer.observe(spinnerRef.current);
            }

            return () => {
                observer.disconnect();
            };
        }
    }, [props.requestMoreData]);

    return (
        <table className={cx(tableStyle, props.alternatingRowColors && alternatingRows)} {...getTableProps()}>
            <thead>
                {headerGroups.map((headerGroup) => (
                    <tr key="header" {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column, index) => {
                            if (props.hasColumnGotCustomSorting?.(column)) {
                                return (
                                    <React.Fragment key={index}>
                                        {props.customSortedColumnHeader?.(column)}
                                    </React.Fragment>
                                );
                            }
                            return (
                                <th
                                    key={index}
                                    className={(column as any).canSort ? `${states} ${sortableHeader}` : null}
                                    {...column.getHeaderProps(
                                        (column as any).getSortByToggleProps({
                                            title: getSortLabelHoverText(column, props.mapColumnIdToName),
                                        }),
                                    )}
                                >
                                    {(column as any).canSort ? (
                                        <SortableHeaderLabel
                                            active={(column as any).isSorted}
                                            direction={(column as any).isSortedDesc ? 'desc' : 'asc'}
                                        >
                                            {column.render('Header')}
                                        </SortableHeaderLabel>
                                    ) : (
                                        <div className={plainHeader}>{column.render('Header')}</div>
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                ))}
            </thead>
            <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                    prepareRow(row);
                    return (
                        <TableRow
                            key={row.id}
                            row={row}
                            hasCustomRowProps={props.hasCustomRowProps}
                            getCustomRowProps={props.getCustomRowProps}
                        />
                    );
                })}
                <tr>
                    {props.hasMoreData && (
                        <td colSpan={columns.length}>
                            <div ref={spinnerRef} className={'spinner'}>
                                <Spinner />
                            </div>
                        </td>
                    )}
                </tr>
            </tbody>
        </table>
    );
}

const TableRow = ({
    row,
    hasCustomRowProps,
    getCustomRowProps,
}: {
    row: Row<Record<string, unknown>>;
    hasCustomRowProps: boolean;
    getCustomRowProps: (row: Row) => Record<string, unknown>;
}) => {
    const rowProps = hasCustomRowProps ? row.getRowProps(getCustomRowProps(row)) : row.getRowProps();
    return (
        <tr {...rowProps}>
            {row.cells.map((cell) => {
                return (
                    <td key={cell.value} {...cell.getCellProps()}>
                        {cell.render('Cell')}
                    </td>
                );
            })}
        </tr>
    );
};
